import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

// Keys for localStorage
const OFFLINE_QUEUE_KEY = 'fencepost_offline_queue';
const CACHED_DATA_KEY = 'fencepost_cached_data';

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Load offline queue from localStorage on mount
  useEffect(() => {
    const savedQueue = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (savedQueue) {
      try {
        setOfflineQueue(JSON.parse(savedQueue));
      } catch (e) {
        console.error('Failed to parse offline queue:', e);
      }
    }
  }, []);

  // Save offline queue to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(offlineQueue));
  }, [offlineQueue]);

  // Sync offline queue - using ref to avoid stale closure
  const syncOfflineQueue = useCallback(async () => {
    if (offlineQueue.length === 0 || isSyncing) return;

    setIsSyncing(true);
    const failedActions = [];
    const token = localStorage.getItem('fencepost_token');

    for (const action of offlineQueue) {
      try {
        const response = await fetch(action.url, {
          method: action.method,
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: action.body ? JSON.stringify(action.body) : undefined
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to sync action:', action, error);
        failedActions.push(action);
      }
    }

    setOfflineQueue(failedActions);
    setLastSyncTime(new Date().toISOString());
    setIsSyncing(false);

    if (failedActions.length === 0) {
      toast.success('All changes synced!');
    } else {
      toast.error(`${failedActions.length} action(s) failed to sync`, {
        description: 'Will retry on next connection.'
      });
    }
  }, [offlineQueue, isSyncing]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online!', {
        description: offlineQueue.length > 0 ? `Syncing ${offlineQueue.length} pending action(s)...` : undefined
      });
      // Trigger sync when coming back online
      if (offlineQueue.length > 0) {
        syncOfflineQueue();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline', {
        description: 'Changes will be saved locally and synced when you reconnect.'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [offlineQueue, syncOfflineQueue]);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                toast.info('App update available', {
                  description: 'Refresh to get the latest version.',
                  action: {
                    label: 'Refresh',
                    onClick: () => window.location.reload()
                  }
                });
              }
            });
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Add action to offline queue
  const queueOfflineAction = useCallback((action) => {
    const queuedAction = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...action
    };
    setOfflineQueue((prev) => [...prev, queuedAction]);
    toast.info('Saved offline', {
      description: 'This will sync when you reconnect.'
    });
    return queuedAction.id;
  }, []);

  // Cache data locally for offline access
  const cacheData = useCallback((key, data) => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHED_DATA_KEY) || '{}');
      cached[key] = {
        data,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(CACHED_DATA_KEY, JSON.stringify(cached));
    } catch (e) {
      console.error('Failed to cache data:', e);
    }
  }, []);

  // Get cached data
  const getCachedData = useCallback((key, maxAge = 24 * 60 * 60 * 1000) => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHED_DATA_KEY) || '{}');
      const entry = cached[key];
      
      if (!entry) return null;
      
      const age = Date.now() - new Date(entry.timestamp).getTime();
      if (age > maxAge) return null;
      
      return entry.data;
    } catch (e) {
      console.error('Failed to get cached data:', e);
      return null;
    }
  }, []);

  // Clear offline queue
  const clearOfflineQueue = useCallback(() => {
    setOfflineQueue([]);
    localStorage.removeItem(OFFLINE_QUEUE_KEY);
  }, []);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      offlineQueue,
      isSyncing,
      lastSyncTime,
      queueOfflineAction,
      syncOfflineQueue,
      cacheData,
      getCachedData,
      clearOfflineQueue,
      pendingCount: offlineQueue.length
    }}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export default OfflineContext;
